import { RefuellingTaskFormPayload } from 'aos-services/src/services/tasks/types/payload/RefuellingTaskFormPayload'
import {
    allRefuellingTaskTypes,
    RefuellingTaskType,
    sortRefuellingTaskTypes,
    translateRefuellingTaskType,
} from 'aos-services/src/services/tasks/types/task/RefuellingTask'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import React, { FC, useMemo } from 'react'
import { Controller, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { refuellingTaskTypeIcon } from '../../../utils'
import { TaskFormField } from '../TaskFormField'
import { TaskOptionChip } from '../TaskOptionChip'
import { TaskTextField } from './partials/TaskTextField'

interface RefuellingTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const RefuellingTaskForm: FC<RefuellingTaskFormProps> = ({ isTaskTemplate }) => {
    const { t: translate } = useTranslation()
    const { control } = useFormContext<RefuellingTaskFormPayload>()
    const payload = useWatch({ control }) as RefuellingTaskFormPayload
    const airplaneTypeField = useController({ name: 'airplaneType', control })
    const vehicleDetailsField = useController({ name: 'vehicleDetails', control })
    const titleField = useController({ name: 'title', control })
    const additionalFields = useMemo(() => {
        if (payload.refuellingTaskType === RefuellingTaskType.REFUELLING) {
            return (
                <Controller
                    control={control}
                    name='airplaneType'
                    render={({ field, fieldState }) => (
                        <TaskTextField
                            keyPrefix='tasks.form.refuelling.aircraft-type'
                            error={
                                fieldState.error
                                    ? translate('form.refuelling.aircraft-type.error', {
                                          ns: 'tasks',
                                      })
                                    : undefined
                            }
                            seleniumLocation='airplaneType'
                            value={field.value}
                            onChangeText={field.onChange}
                        />
                    )}
                />
            )
        }
        if (payload.refuellingTaskType === RefuellingTaskType.EQUIPMENT_MAINTENANCE) {
            return (
                <Controller
                    control={control}
                    name='vehicleDetails'
                    render={({ field, fieldState }) => (
                        <TaskTextField
                            keyPrefix='tasks.form.refuelling.vehicle-details'
                            error={
                                fieldState.error
                                    ? translate('form.refuelling.vehicle-details.error', {
                                          ns: 'tasks',
                                      })
                                    : undefined
                            }
                            seleniumLocation='vehicleDetails'
                            value={field.value}
                            onChangeText={field.onChange}
                        />
                    )}
                />
            )
        }
        return null
    }, [control, payload.refuellingTaskType, translate])

    const handleChange = (refuellingTaskType: RefuellingTaskType) => {
        airplaneTypeField.field.onChange(undefined)
        vehicleDetailsField.field.onChange(undefined)
        titleField.field.onChange(translateRefuellingTaskType(translate)(refuellingTaskType))
    }

    return (
        <>
            <Controller
                control={control}
                name='refuellingTaskType'
                render={({ field, fieldState }) => (
                    <TaskFormField
                        label={translate('form.refuelling-task-type.label', { ns: 'tasks' })}
                        required={!isTaskTemplate}
                        error={
                            fieldState.error
                                ? translate('form.refuelling-task-type.error', { ns: 'tasks' })
                                : undefined
                        }
                        seleniumLocation='category'
                    >
                        <div className='grid grid-cols-4 gap-2'>
                            {sortRefuellingTaskTypes(allRefuellingTaskTypes, translate).map(
                                item => (
                                    <TaskOptionChip
                                        key={item}
                                        orientation='vertical'
                                        icon={refuellingTaskTypeIcon[item]}
                                        label={translateRefuellingTaskType(translate)(item)}
                                        isSelected={field.value === item}
                                        onPress={() => {
                                            field.onChange(item)
                                            handleChange(item)
                                        }}
                                    />
                                ),
                            )}
                        </div>
                    </TaskFormField>
                )}
            />
            {additionalFields}
        </>
    )
}
